import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  type CrmOrganization,
  type ProContext,
  type ProOrganization,
  type ProWorkspace,
  type ProWorkspaceType,
} from "./api";
import { useAuth } from "./auth-context";

interface CrmState {
  status: "loading" | "ready" | "error";
  context: ProContext | null;
  activeOrganization: CrmOrganization | null;
  activeProOrganization: ProOrganization | null;
  activeWorkspace: ProWorkspace | null;
  requiresSelection: boolean;
  selectWorkspace: (organizationId: string, workspace: ProWorkspaceType) => Promise<void>;
  setActiveOrganizationId: (id: string) => void;
  error: string | null;
  reload: () => Promise<void>;
}

const CrmContextState = createContext<CrmState | null>(null);
const ORGANIZATION_STORAGE_KEY = "tappet-pro-organization";
const WORKSPACE_STORAGE_KEY = "tappet-pro-workspace";
const LEGACY_ORGANIZATION_KEY = "tappet-crm-organization";

function validSelection(
  context: ProContext,
  organizationId: string,
  workspace: string,
): { organizationId: string; workspace: ProWorkspaceType } | null {
  const organization = context.organizations.find((item) => item.id === organizationId);
  const selected = organization?.availableWorkspaces.find((item) => item.type === workspace);
  return organization && selected ? { organizationId: organization.id, workspace: selected.type } : null;
}

function onlySelection(context: ProContext): { organizationId: string; workspace: ProWorkspaceType } | null {
  const choices = context.organizations.flatMap((organization) =>
    organization.availableWorkspaces.map((workspace) => ({
      organizationId: organization.id,
      workspace: workspace.type,
    })),
  );
  return choices.length === 1 ? (choices[0] ?? null) : null;
}

export function CrmProvider({ children }: { children: ReactNode }): ReactNode {
  const { status: authStatus, user } = useAuth();
  const [context, setContext] = useState<ProContext | null>(null);
  const [organizationId, setOrganizationId] = useState("");
  const [workspaceType, setWorkspaceType] = useState<ProWorkspaceType | null>(null);
  const [status, setStatus] = useState<CrmState["status"]>("loading");
  const [error, setError] = useState<string | null>(null);

  const applySelection = useCallback((selection: { organizationId: string; workspace: ProWorkspaceType } | null) => {
    setOrganizationId(selection?.organizationId ?? "");
    setWorkspaceType(selection?.workspace ?? null);
    if (selection) {
      localStorage.setItem(ORGANIZATION_STORAGE_KEY, selection.organizationId);
      localStorage.setItem(WORKSPACE_STORAGE_KEY, selection.workspace);
    } else {
      localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
    }
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    if (authStatus !== "authenticated") return;
    setStatus("loading");
    setError(null);
    try {
      const next = await api.proContext();
      setContext(next);
      const storedOrganization =
        localStorage.getItem(ORGANIZATION_STORAGE_KEY) ?? localStorage.getItem(LEGACY_ORGANIZATION_KEY) ?? "";
      const storedWorkspace = localStorage.getItem(WORKSPACE_STORAGE_KEY) ?? "VETERINARY";
      const selection = validSelection(next, storedOrganization, storedWorkspace) ?? onlySelection(next);
      if (selection) await api.selectProContext(selection.organizationId, selection.workspace);
      applySelection(selection);
      setStatus("ready");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos cargar el contexto profesional.");
      setStatus("error");
    }
  }, [applySelection, authStatus, user?.id]);

  useEffect(() => {
    if (authStatus === "loading") {
      setStatus("loading");
      return;
    }
    if (authStatus === "anonymous") {
      setContext(null);
      setError(null);
      applySelection(null);
      setStatus("ready");
      return;
    }
    void reload();
  }, [applySelection, authStatus, user?.id, reload]);

  const activeProOrganization =
    context?.organizations.find((organization) => organization.id === organizationId) ?? null;
  const activeWorkspace =
    activeProOrganization?.availableWorkspaces.find((workspace) => workspace.type === workspaceType) ?? null;
  const activeOrganization: CrmOrganization | null =
    activeProOrganization && activeWorkspace
      ? {
          id: activeProOrganization.id,
          name: activeProOrganization.name,
          type: activeProOrganization.organizationType,
          role: activeWorkspace.role,
        }
      : null;

  const selectWorkspace = useCallback(
    async (nextOrganizationId: string, nextWorkspace: ProWorkspaceType): Promise<void> => {
      await api.selectProContext(nextOrganizationId, nextWorkspace);
      applySelection({ organizationId: nextOrganizationId, workspace: nextWorkspace });
    },
    [applySelection],
  );

  const value = useMemo<CrmState>(
    () => ({
      status,
      context,
      activeOrganization,
      activeProOrganization,
      activeWorkspace,
      requiresSelection: Boolean(context?.organizations.length) && !activeWorkspace,
      error,
      reload,
      selectWorkspace,
      setActiveOrganizationId: (id: string) => {
        const organization = context?.organizations.find((item) => item.id === id);
        const preferred = organization?.availableWorkspaces.find(
          (item) => item.type === organization.primaryVertical,
        ) ?? organization?.availableWorkspaces[0];
        if (organization && preferred) void selectWorkspace(organization.id, preferred.type);
      },
    }),
    [status, context, activeOrganization, activeProOrganization, activeWorkspace, error, reload, selectWorkspace],
  );

  return <CrmContextState.Provider value={value}>{children}</CrmContextState.Provider>;
}

export function useCrm(): CrmState {
  const value = useContext(CrmContextState);
  if (!value) throw new Error("useCrm debe usarse dentro de CrmProvider");
  return value;
}
